import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Shared intro loader with a two-tier cache (in-memory Map + localStorage)
 * so navigating between intro / overview pages (Welcome, Launch Blueprint,
 * Course Overview, Database Overview, Financial Tools Intro, etc.) never
 * flashes the hardcoded fallback copy while the DB override loads.
 *
 * ── Why the localStorage tier ─────────────────────────────────────────
 * Before this hook the module-level `Map` reset on every hard reload,
 * new tab, direct URL, or back-button restore — so returning founders
 * saw the fallback flash again the moment their browser evicted the JS
 * memory. Preview hid this because the DB round-trip was ~50ms; prod's
 * CDN + cross-region hop is 300-800ms, making the same flash clearly
 * visible. Persisting the resolved intro to localStorage kills the
 * flash on every visit AFTER the first one on this device — the
 * `useState` initializer reads synchronously from disk before React's
 * first commit, so the very first frame paints real content.
 *
 * ── Freshness ─────────────────────────────────────────────────────────
 * A background refresh still fires on every mount, so admin edits reach
 * the caller within one navigation. Cached entries older than 7 days
 * are ignored to avoid stale copy on machines that never come back.
 */
const introCache = new Map(); // key → merged intro (in-memory tier)
const inflight   = new Map(); // key → in-flight promise (dedupe)

// Cache-storage config. `CACHE_VERSION` lets us invalidate every founder's
// disk cache in one deploy if the payload shape changes (e.g. new field).
const STORAGE_PREFIX  = "cpg:intro:v1:";
const CACHE_MAX_AGE   = 7 * 24 * 60 * 60 * 1000; // 7 days

function storageKey(key) { return `${STORAGE_PREFIX}${key}`; }

function readFromStorage(key) {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > CACHE_MAX_AGE) return null;
    return parsed.value || null;
  } catch {
    return null;
  }
}

function writeToStorage(key, value) {
  try {
    window.localStorage.setItem(
      storageKey(key),
      JSON.stringify({ ts: Date.now(), value }),
    );
  } catch {
    /* quota exceeded / private mode — safe to ignore, memory tier still works */
  }
}

function mergeOverrides(fallback, raw) {
  if (!raw) return fallback;
  // Strip Mongo/system fields that would collide with reserved React
  // props (`key`) or leak internal state.
  const { key: _k, updated_at: _u, updated_by_email: _e, ...rest } = raw;
  // Only accept non-empty overrides so empty / placeholder DB values
  // (e.g. leftover "TEST" migration data) never wipe out well-written
  // fallback copy.
  const overrides = Object.fromEntries(
    Object.entries(rest).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : (v !== "" && v != null),
    ),
  );
  return { ...fallback, ...overrides };
}

// Synchronous cache lookup used by `useState` initializer. Checks
// in-memory first (fast, no JSON parse), then localStorage. Hydrates
// the in-memory tier so subsequent mounts skip the disk read.
function hydrateFromCache(key) {
  const mem = introCache.get(key);
  if (mem) return mem;
  const disk = readFromStorage(key);
  if (disk) {
    introCache.set(key, disk);
    return disk;
  }
  return null;
}

export function useIntro(key, fallback) {
  // Read the disk / memory cache SYNCHRONOUSLY during the initializer
  // so returning founders paint real content on the very first frame.
  // First-time visitors (incognito, new device, cleared storage) get
  // `null` for `intro` and `ready=false` so the consumer can render a
  // loading skeleton until the DB round-trip settles — this kills the
  // "hardcoded fallback flash" complaint on cold loads.
  const cached = hydrateFromCache(key);
  const [state, setState] = useState(() => ({
    intro: cached || null,
    ready: !!cached,
  }));

  useEffect(() => {
    let cancelled = false;
    let p = inflight.get(key);
    if (!p) {
      p = api.get(`/intros/${key}`).then((r) => r.data).catch(() => null);
      inflight.set(key, p);
      p.finally(() => { inflight.delete(key); });
    }
    p.then((raw) => {
      if (cancelled) return;
      // If the request succeeded, merge over the fallback. If it
      // failed (null), degrade to the fallback so the page is never
      // permanently stuck on a spinner in production incidents.
      const merged = mergeOverrides(fallback, raw);
      introCache.set(key, merged);
      writeToStorage(key, merged);
      setState({ intro: merged, ready: true });
    });
    return () => { cancelled = true; };
    // `fallback` is a static object per page — don't include it in
    // deps to avoid re-fetches when its reference changes across
    // hot reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
